import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import { Box, Button, Stack, Tab, Tabs, Typography } from "@mui/material";
import { AxiosError } from "axios";
import { observer } from "mobx-react";
import moment from "moment";
import { SyntheticEvent, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { finalize } from "rxjs";
import { getFormValidation } from "src/Shared/Components";
import GridFormControl from "src/Shared/Components/GridFormControl/GridFormControl";
import Title from "src/Shared/Components/Title/Title";
import { EDITOR_TYPES } from "src/Shared/Enums";
import { useModal, useStore } from "src/Shared/Hooks";
import { IGridFormControlProps } from "src/Shared/Interfaces/IGridFormControlProps.interface";
import { ToastService } from "src/Shared/Services";
import DateTimeUtils from "src/Shared/Tools/DateTimeUtils";
import { ActionTaskType, ActionType, SLAStatus } from "../../Enums";
import { ClientStatus } from "../../Enums/ClientStatus.enum";
import { IRouteParams } from "../../Interfaces/IRouteParams";
import { ISLADocument } from "../../Interfaces/ISLAInfo";
import CompanyService from "../../Services/Company.service";
import DocumentsTab from "./DocumentTab/DocumentTab";
import { fields } from "./fields";
import ReviewActionsTab from "./ReviewAction/ReviewActionTab";
import SLAComments from "./SLAComment/SLAComments";
import { SLA_CONFIG } from "./SLAManagement.config";
import { useStyles } from "./SLAManagement.styles";
import WarningPopup from 'src/Shared/Components/WarningPopup/WarningPopup';

const FORM_VALIDATION_FIELDS = [
    "slaExpiryDate",
    "slaReminderDate",
    "priceReviewDate",
    "slaReminderSentTo",
] as const;

const getFormFieldKeyForRequirement = (requiredFieldKey: string) => {
    switch (requiredFieldKey) {
        case "expiry":
            return "slaExpiryDate";
        case "priceReviewDate":
            return "priceReviewDate";
        case "slaReminderDate":
            return "slaReminderDate";
        case "slaReminderSentTo":
            return "slaReminderSentTo";
        default:
            return null;
    }
};

const SLAManagement: React.FC = () => {
    const modal = useModal();
    const classes = useStyles();
    const { companyRowID } = useParams<Partial<IRouteParams>>();

    const {
        rootStore: {
            commonStore: { slaStatus, actionTypeList, actionTaskTypeList },
            addCommentStore: { btnLoading },
            salesActionStore: { refreshSLAReviewActions },
            companyStore: {
                companyOverview,
                companyRelationshipManager,
                getCompanyRelationshipManager,
                slaInfo,
                getSLAInfo,
            },
            uiStore,
        },
    } = useStore();

    const slaDetailsForm = useMemo(() => getFormValidation(fields), []);
    const getField = (key: string) => slaDetailsForm.$(key);

    const filteredSLAStatusOptions = useMemo(() => {
        const currentVal = Number(getField("slaStatusID").value);
        const isCurrentlyPendingUpload = currentVal === SLAStatus.CurrentSLAPendingDocumentUpload;
        const baseOptions = slaStatus.filter(option => Number(option.value) !== SLAStatus.CurrentSLAPendingDocumentUpload);
        
        if (isCurrentlyPendingUpload) {
            const hasPendingOption = slaStatus.some(option => Number(option.value) === SLAStatus.CurrentSLAPendingDocumentUpload);
            if (hasPendingOption) {
                return slaStatus;
            } else {
                return [...baseOptions, { label: "Current SLA - Pending Document Upload", value: String(SLAStatus.CurrentSLAPendingDocumentUpload) }];
            }
        }
        return baseOptions;
    }, [slaStatus, getField("slaStatusID").value]);

    const [sectionTab, setSectionTab] = useState(0);
    const [showSectionErrors, setShowSectionErrors] = useState(false);
    const [slaExpiryDate, setslaExpiryDate] = useState(null);
    const [hasCurrentDocument, setHasCurrentDocument] = useState(false);
    const [slaDocuments, setSlaDocuments] = useState<ISLADocument[]>([]);

    const currentStatus = Number(
        getField("slaStatusID").value || SLAStatus.NotStarted,
    ) as SLAStatus;

    const slaReminderSentToName = companyRelationshipManager.find(x => x.value === getField("slaReminderSentTo")?.value)?.label;
    const actionTypeName = actionTypeList.find(x => x.value === ActionType.CRMAction)?.label;
    const actionTaskTypeName = actionTaskTypeList.find(x => x.value === ActionTaskType.CRMAction_SLAReview)?.label;

    const currentStatusConfig = SLA_CONFIG[currentStatus];
    const isClientRefused = currentStatus === SLAStatus.ClientRefused;
    const isCurrentSla = currentStatus === SLAStatus.CurrentSLA;

    const getRequiredFormFieldKeys = () => {
        if (companyOverview?.clientStatusID === ClientStatus.Prospect) {
            return [];
        }

        const baseRequiredKeys = ["companyRelationshipManagerID"];

        const keysToExclude =
            slaInfo?.slaStatusID === SLAStatus.ClientRefused
                ? new Set(["slaReminderDate", "slaReminderSentTo"])
                : null;

        const mappedKeys = currentStatusConfig.required.reduce<string[]>(
            (acc, requiredFieldKey) => {
                const key = getFormFieldKeyForRequirement(requiredFieldKey);
                if (!key) return acc;

                if (keysToExclude?.has(key)) {
                    return acc;
                }

                acc.push(key);
                return acc;
            },
            []
        );

        return Array.from(new Set([...baseRequiredKeys, ...mappedKeys]));
    };

    const requiredFormFieldKeys = getRequiredFormFieldKeys();

    const applyDateRules = (fieldKey: string) => {
        const isRequired = requiredFormFieldKeys.includes(fieldKey);

        const originalValue = slaInfo?.[fieldKey];
        const currentValue = getField(fieldKey).value;
        const today = moment().startOf("day");

        const parsedOriginal = originalValue
            ? moment(originalValue, "DD/MM/YYYY", true)
            : null;

        const parsedCurrent = currentValue
            ? moment.isMoment(currentValue)
                ? currentValue.clone().startOf("day")
                : moment(currentValue, ["DD/MM/YYYY", moment.ISO_8601], true).startOf("day")
            : null;

        const hasOriginalPastDate =
            !!parsedOriginal &&
            parsedOriginal.isValid() &&
            parsedOriginal.isBefore(today, "day");

        const isSameAsOriginalPastDate =
            hasOriginalPastDate &&
            !!parsedCurrent &&
            parsedCurrent.isValid() &&
            parsedCurrent.isSame(parsedOriginal, "day");

        const rules = [];
        if (isRequired) rules.push("required");
        rules.push("validDate");

        if (!isSameAsOriginalPastDate) {
            rules.push("minDateValidator");
        }

        getField(fieldKey).set("rules", rules.join("|"));
    };

    useEffect(() => {
        FORM_VALIDATION_FIELDS.forEach((fieldKey) => {
            getField(fieldKey).set("rules", "");
        });
        if (companyOverview?.clientStatusID !== ClientStatus.Prospect) {
            requiredFormFieldKeys.forEach((fieldKey) => {
                if (["slaExpiryDate", "slaReminderDate", "priceReviewDate"].includes(fieldKey)) {
                    applyDateRules(fieldKey);
                } else if (fieldKey !== "companyRelationshipManagerID") {
                    getField(fieldKey).set("rules", "required");
                }
            });

            getField("companyRelationshipManagerID").set("rules", "required");
        } else {
            getField("companyRelationshipManagerID").set("rules", "");
            getField("slaStatusID").set("rules", "");
        }

        getField("slaExpiryDate").set(
            "disabled",
            currentStatusConfig.expiry === "readonly",
        );
        getField("priceReviewDate").set(
            "disabled",
            currentStatusConfig.priceReviewDate === "readonly",
        );
        getField("slaReminderDate").set("disabled", false);
        getField("slaReminderSentTo").set("disabled", false);

        if (isClientRefused) {
            getField("slaReminderSentTo").set(
                "value",
                getField("companyRelationshipManagerID").value ?? "",
            );
        }

        slaDetailsForm.validate();
    }, [currentStatus, companyOverview?.clientStatusID, slaInfo]);

    useEffect(() => {
        getCompanyRelationshipManager(companyRowID);
    }, []);

    useEffect(() => {
        if (!companyRowID) return;
        getSLAInfo(companyRowID);
    }, [companyRowID]);

    useEffect(() => {
        if (!slaInfo) return;

        getField("slaStatusID").set("value", slaInfo.slaStatusID ?? "");
        getField("companyRelationshipManagerID").set(
            "value",
            slaInfo.companyRelationshipManagerID ?? companyOverview?.primaryCompanyRelationshipManagerId ?? "",
        );
        getField("slaExpiryDate").set("value", slaInfo.slaExpiryDate ?? null);
        getField("priceReviewDate").set("value", slaInfo.priceReviewDate ?? null);
        getField("slaReminderDate").set("value", slaInfo.slaReminderDate ?? null);
        getField("slaReminderSentTo").set("value", slaInfo.slaReminderSentTo ?? "");

        setSlaDocuments(slaInfo.slaDocuments || []);
        setHasCurrentDocument(!!slaInfo.slaDocuments?.some((x) => x.isCurrent));

        applyDateRules("slaExpiryDate");
        applyDateRules("priceReviewDate");
        applyDateRules("slaReminderDate");

        slaDetailsForm.validate();
    }, [slaInfo]);

    useEffect(() => {
        const field = getField("companyRelationshipManagerID");
        if (field && companyOverview?.primaryCompanyRelationshipManagerId) {
            field.set("value", slaInfo?.companyRelationshipManagerID ?? companyOverview?.primaryCompanyRelationshipManagerId);
        }
    }, [companyOverview]);
    const navigate = useNavigate();
    const checkHasUnsavedChanges = () => {
        if (!slaInfo) return false;

        const formatStr = (val: any) => val ? String(val).trim() : "";

        if (formatStr(getField("slaStatusID").value) !== formatStr(slaInfo.slaStatusID)) return true;

        const originalManagerID = slaInfo.companyRelationshipManagerID ?? companyOverview?.primaryCompanyRelationshipManagerId;
        if (formatStr(getField("companyRelationshipManagerID").value) !== formatStr(originalManagerID)) return true;

        const formatOrNull = (val: any) => {
            if (!val) return "";
            if (moment.isMoment(val)) return val.format("DD/MM/YYYY");
            if (typeof val === "string") {
                const parsed = moment(val, ["DD/MM/YYYY", moment.ISO_8601], true);
                if (parsed.isValid()) return parsed.format("DD/MM/YYYY");
                const fallback = moment(val);
                if (fallback.isValid()) return fallback.format("DD/MM/YYYY");
            }
            return String(val).trim();
        };

        if (formatOrNull(getField("slaExpiryDate").value) !== formatOrNull(slaInfo.slaExpiryDate)) return true;
        if (formatOrNull(getField("priceReviewDate").value) !== formatOrNull(slaInfo.priceReviewDate)) return true;
        if (formatOrNull(getField("slaReminderDate").value) !== formatOrNull(slaInfo.slaReminderDate)) return true;
        if (formatStr(getField("slaReminderSentTo").value) !== formatStr(slaInfo.slaReminderSentTo)) return true;

        const originalDocs = slaInfo.slaDocuments || [];
        const currentDocs = slaDocuments || [];

        if (originalDocs.length !== currentDocs.length) return true;

        for (let i = 0; i < currentDocs.length; i++) {
            const currentDoc = currentDocs[i];
            if (currentDoc.file) return true;

            const originalDoc = originalDocs.find(d => d.slaAttachmentID === currentDoc.slaAttachmentID);
            if (!originalDoc) return true;

            if (currentDoc.isCurrent !== originalDoc.isCurrent) return true;
            if (formatOrNull(currentDoc.expiryDate) !== formatOrNull(originalDoc.expiryDate)) return true;
        }

        return false;
    };

    const isBypassRef = useRef(false);

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (!isBypassRef.current && checkHasUnsavedChanges()) {
                e.preventDefault();
                e.returnValue = "";
            }
        };

        const handleClick = (e: MouseEvent) => {
            if (isBypassRef.current || !checkHasUnsavedChanges()) return;

            const target = e.target as HTMLElement;
            const anchor = target.closest("a");

            if (anchor && anchor.href && !anchor.target) {
                const url = new URL(anchor.href);
                // Intercept internal links
                if (url.origin === window.location.origin) {
                    e.preventDefault();
                    e.stopPropagation();

                    modal.open(
                        <WarningPopup
                            title="Unsaved Changes"
                            message="You have unsaved changes. Do you want to stay on this page or leave?"
                            confirmButtonText="Leave"
                            cancelButtonText="Stay"
                            defaultClose={false}
                            onConfirm={(confirm) => {
                                modal.close();
                                if (confirm) {
                                    isBypassRef.current = true;
                                    navigate(url.pathname + url.search + url.hash);
                                }
                            }}
                        />
                    );
                }
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        document.addEventListener("click", handleClick, true);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            document.removeEventListener("click", handleClick, true);
        };
    });

    const handleDateChange = (value: any, fieldKey: string) => {
        getField(fieldKey).set("value", value);
        applyDateRules(fieldKey);
        slaDetailsForm.validate();
        slaDetailsForm.showErrors();
    };
    const badgeClasses = {
        "sb-gray": classes.badgeGray,
        "sb-blue": classes.badgeBlue,
        "sb-red": classes.badgeRed,
        "sb-green": classes.badgeGreen,
        "sb-amber": classes.badgeAmber,
    };

    const barClasses = {
        "alert-gray": classes.alertGray,
        "alert-blue": classes.alertBlue,
        "alert-red": classes.alertRed,
        "alert-amber": classes.alertAmber,
    };

    const detailGridProps: IGridFormControlProps = {
        row: 2,
        col: 2,
        spacing: 2,
        itemResponsive: {
            lg: 6,
            md: 6,
            sm: 12,
        },
        inputControls: [
            {
                field: getField("slaStatusID"),
                fieldKey: "slaStatusID",
                type: EDITOR_TYPES.DROPDOWN,
                options: filteredSLAStatusOptions,
            },
            {
                field: getField("companyRelationshipManagerID"),
                fieldKey: "companyRelationshipManagerID",
                type: EDITOR_TYPES.DROPDOWN,
                options: companyRelationshipManager,
            },
            {
                field: getField("slaExpiryDate"),
                fieldKey: "slaExpiryDate",
                type: EDITOR_TYPES.DATE,
                hidden: currentStatusConfig.expiry === "hidden",
                minDate: moment(new Date()),
                onValueChange(date) {
                    handleDateChange(date, "slaExpiryDate");
                },
                inputProps: {
                    readOnly: currentStatusConfig.expiry === "readonly",
                },
            },
            {
                field: getField("priceReviewDate"),
                fieldKey: "priceReviewDate",
                type: EDITOR_TYPES.DATE,
                minDate: moment(new Date()),
                hidden: currentStatusConfig.priceReviewDate === "hidden",
                inputProps: {
                    readOnly: currentStatusConfig.priceReviewDate === "readonly",
                },
                onValueChange(date) {
                    handleDateChange(date, "priceReviewDate");
                },
            },
        ],
        form: slaDetailsForm,
    };

    const followUpGridProps: IGridFormControlProps = {
        row: 2,
        col: 1,
        spacing: 2,
        itemResponsive: {
            lg: 6,
            md: 6,
            sm: 12,
        },
        inputControls: [
            {
                field: getField("slaReminderDate"),
                fieldKey: "slaReminderDate",
                type: EDITOR_TYPES.DATE,
                minDate: moment(new Date()),
                onValueChange(date) {
                    handleDateChange(date, "slaReminderDate");
                },
            },
            {
                field: getField("slaReminderSentTo"),
                fieldKey: "slaReminderSentTo",
                type: EDITOR_TYPES.DROPDOWN,
                options: companyRelationshipManager,

            },
        ],
        form: slaDetailsForm,
    };

    const automatedReminderItems = useMemo(() => {
        if (!isCurrentSla) return [];

        const relationshipHolder = companyRelationshipManager.find(
            (x) => x.value === slaInfo.companyRelationshipManagerID,
        )?.label;

        const expiryDate = getField("slaExpiryDate").value;
        const priceReviewDate = getField("priceReviewDate").value;

        const items = [];

        if (expiryDate) {
            items.push({
                key: "sla-review",
                text: `An SLA Review action will be automatically created 90 days before ${moment.isMoment(expiryDate) ? expiryDate.format("DD/MM/YYYY") : expiryDate}, assigned to ${relationshipHolder}`,
            });
        }

        if (priceReviewDate) {
            items.push({
                key: "price-review",
                text: `A price review SLA Review action will be automatically created 90 days before ${moment.isMoment(priceReviewDate) ? priceReviewDate.format("DD/MM/YYYY") : priceReviewDate}, assigned to ${relationshipHolder}`,
            });
        }

        return items;
    }, [
        isCurrentSla,
        getField("slaExpiryDate").value,
        getField("priceReviewDate").value,
        getField("companyRelationshipManagerID").value,
        slaExpiryDate
    ]);

    const isFieldCompleted = (fieldKey: string) => {
        const field = getField(fieldKey);
        const value = field?.value;

        if (value === null || value === undefined) return false;

        if (field?.hasError) return false;

        if (typeof value === "string") {
            return value.trim().length > 0;
        }

        if (Array.isArray(value)) {
            return value.length > 0;
        }

        return true;
    };

    const completedFormRequiredCount = requiredFormFieldKeys.filter((fieldKey) =>
        isFieldCompleted(fieldKey),
    ).length;

    const isDocumentRequired =
        companyOverview?.clientStatusID !== ClientStatus.Prospect &&
        currentStatus === SLAStatus.CurrentSLA;

    const completedDocumentRequiredCount = isDocumentRequired
        ? hasCurrentDocument
            ? 1
            : 0
        : 0;

    const totalDocumentRequiredCount = isDocumentRequired ? 1 : 0;

    const completedRequiredCount =
        completedFormRequiredCount + completedDocumentRequiredCount;

    const totalRequiredCount =
        requiredFormFieldKeys.length + totalDocumentRequiredCount;

    const completionPercent =
        totalRequiredCount === 0
            ? 100
            : Math.round((completedRequiredCount / totalRequiredCount) * 100);

    const isValidationComplete =
        totalRequiredCount === 0 || completedRequiredCount === totalRequiredCount;

    const progressBarColor = isValidationComplete ? "#2e7d32" : "#ef7d00";

    const handleSectionTabChange = (_: SyntheticEvent, newValue: number) => {
        setSectionTab(newValue);
    };

    const saveSLAChanges = () => {
        setShowSectionErrors(true);
        slaDetailsForm.validate();
        slaDetailsForm.showErrors();

        if (
            !slaDetailsForm.isValid ||
            (isDocumentRequired && !hasCurrentDocument)
        ) {
            return;
        }
        const formData = new FormData();

        formData.append("CompanyRowID", companyRowID || "");
        formData.append("CompanySLAAdditionalInfoID", "0");
        formData.append("SLAStatusID", String(getField("slaStatusID").value ?? ""));
        formData.append(
            "CompanyRelationshipManagerID",
            String(getField("companyRelationshipManagerID").value ?? ""),
        );
        formData.append(
            "SLAExpiryDate",
            getField("slaExpiryDate").value
                ? DateTimeUtils.toApiDate(getField("slaExpiryDate").value)
                : "",
        );
        formData.append(
            "PriceReviewDate",
            getField("priceReviewDate").value
                ? DateTimeUtils.toApiDate(getField("priceReviewDate").value)
                : "",
        );
        formData.append(
            "SLAReminderDate",
            getField("slaReminderDate").value
                ? DateTimeUtils.toApiDate(getField("slaReminderDate").value)
                : "",
        );
        formData.append(
            "SLAReminderSentTo",
            String(getField("slaReminderSentTo").value ?? ""),
        );

        if (slaDocuments && slaDocuments.length > 0) {
            slaDocuments.forEach((doc, index) => {
                formData.append(
                    `Documents[${index}].SLAAttachmentID`,
                    String(doc.slaAttachmentID ?? 0),
                );
                formData.append(
                    `Documents[${index}].ExpiryDate`,
                    DateTimeUtils.toApiDate(doc.expiryDate) ?? "",
                );
                formData.append(
                    `Documents[${index}].IsCurrent`,
                    String(doc.isCurrent ?? false),
                );
                formData.append(`Documents[${index}].Size`, doc.size ?? "");
                formData.append(`Documents[${index}].Name`, doc.name ?? "");
                formData.append(
                    `Documents[${index}].AttachmentUrl`,
                    doc.attachmentUrl ?? "",
                );
                formData.append(
                    `Documents[${index}].AttachmentTypeID`,
                    String(doc.attachmentTypeID ?? 0),
                );

                if (doc.file) {
                    formData.append(`Documents[${index}].File`, doc.file);
                }
            });
        }

        uiStore.setGlobalLoader(true);

        CompanyService.updateSLAInfo(formData)
            .pipe(
                finalize(() => {
                    uiStore.setGlobalLoader(false);
                }),
            )
            .subscribe({
                next: (data: string) => {
                    slaDetailsForm.reset();
                    ToastService.success(data);
                    getSLAInfo(companyRowID);
                    refreshSLAReviewActions(companyRowID);
                },
                error: (error: AxiosError) => ToastService.axiosError(error),
            });
    };

    const syncExpiryDateFromDocument = (newExpiryDate: string | null) => {
        if (newExpiryDate) {
            getField("slaExpiryDate").set("value", newExpiryDate);
            slaDetailsForm.validate(); // optional, re-run validation
        }
        const currentVal = Number(getField("slaStatusID").value);
        if (currentVal === SLAStatus.InProgress || currentVal === SLAStatus.CurrentSLAPendingDocumentUpload) {
            getField("slaStatusID").set("value", SLAStatus.CurrentSLA);
        }
    };

    const syncPriceReviewDateFromDocument = (date: string | null) => {
        if (date) {
            getField("priceReviewDate").set("value", date);
            slaDetailsForm.validate();
        }
    };

    return (
        <>
            <Box className={classes.root}>
                <Box className={classes.viewButtons}>
                    <Box className={classes.headerLeft}>
                        <Title
                            backButtonEnabled={false}
                            title="SLA Management"
                            variant="h6"
                        />
                        <Box sx={{ color: "#888580", fontSize: "13px" }}>
                            <Typography variant="body1">
                                Manage SLA status, documents and review actions
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex" }}>
                        <Box
                            className={`${classes.statusBadge} ${badgeClasses[currentStatusConfig.badge[0]]
                                }`}
                        >
                            <Typography variant="body1" className={`${classes.statusText}`}>
                                {currentStatusConfig.badge[1]}
                            </Typography>
                        </Box>

                        <Button variant="contained" onClick={saveSLAChanges}>
                            Save Changes
                        </Button>
                    </Box>
                </Box>

                {companyOverview?.clientStatusID === ClientStatus.Prospect && (
                    <Box
                        sx={(theme) => ({
                            borderRadius: theme.spacing(1),
                            border: "1px solid #D1D5DB",
                            mt: theme.spacing(2),
                            px: theme.spacing(2),
                            py: theme.spacing(1.5),
                            color: "#1a73e8",
                        })}
                    >
                        <Typography className={classes.statusBarText}>
                            SLA management is optional for prospects. You may set a status of
                            In Progress if negotiations have commenced, but no fields are
                            mandatory.
                        </Typography>
                    </Box>
                )}

                <Box
                    className={`${classes.statusBar} ${barClasses[currentStatusConfig.bar[0]]
                        }`}
                >
                    <Typography className={classes.statusBarText}>
                        {currentStatusConfig.bar[1]}
                    </Typography>
                </Box>

                <Box sx={{ mt: 2, mb: 2 }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 0.75,
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                mr: 2,
                                height: 4,
                                backgroundColor: "#E5E7EB",
                                borderRadius: 999,
                                overflow: "hidden",
                            }}
                        >
                            <Box
                                sx={{
                                    width: `${completionPercent}%`,
                                    height: "100%",
                                    backgroundColor: progressBarColor,
                                    borderRadius: 999,
                                    transition: "width 0.2s ease",
                                }}
                            />
                        </Box>

                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: 13,
                                color: "#6B7280",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {completedRequiredCount} of {totalRequiredCount} required
                        </Typography>
                    </Box>

                    {showSectionErrors && !isValidationComplete && (
                        <Box
                            sx={{
                                border: "1px solid #FCA5A5",
                                backgroundColor: "#FEF2F2",
                                borderRadius: 2,
                                px: 2,
                                py: 1.5,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Typography sx={{ color: "#DC2626", fontSize: 16 }}>ⓘ</Typography>
                            <Typography sx={{ color: "#DC2626", fontSize: 14 }}>
                                Please complete all required fields before saving.
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box className={classes.sectionTitle}>
                    <Title backButtonEnabled={false} title="SLA Details" />
                    <GridFormControl {...detailGridProps} />
                </Box>

                {isClientRefused && slaInfo?.slaStatusID !== SLAStatus.ClientRefused && (
                    <Box className={classes.sectionCard}>
                        <Box className={classes.followUpHeader}>
                            <Title backButtonEnabled={false} title="Follow-up Reminder" />
                            <Box className={classes.followUpBadge}>
                                <Typography variant="body2">CREATES ACTION ON SAVE</Typography>
                            </Box>
                        </Box>

                        <GridFormControl {...followUpGridProps} />

                        {slaInfo.slaStatusID !== SLAStatus.ClientRefused && getField("slaReminderDate")?.value && (
                            <Box className={classes.actionPreview}>
                                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                    <CheckCircleTwoToneIcon sx={{ color: "#1a73e8" }} />
                                    <Typography variant="subtitle2" fontWeight={600} color="#1a73e8">
                                        ACTION WILL BE CREATED ON SAVE
                                    </Typography>
                                </Stack>

                                <Stack spacing={1}>
                                    <Stack direction="row">
                                        <Typography className={classes.labelStyle}>Type</Typography>
                                        <Typography>{actionTaskTypeName}</Typography>
                                    </Stack>

                                    <Stack direction="row">
                                        <Typography className={classes.labelStyle}>Category</Typography>
                                        <Typography>{actionTypeName}</Typography>
                                    </Stack>

                                    <Stack direction="row">
                                        <Typography className={classes.labelStyle}>Detail</Typography>
                                        <Typography>SLA follow-up — Client Refused</Typography>
                                    </Stack>

                                    <Stack direction="row">
                                        <Typography className={classes.labelStyle}>Due date</Typography>
                                        <Typography> {getField("slaReminderDate")?.value
                                            ? (moment.isMoment(getField("slaReminderDate").value) ? getField("slaReminderDate").value.format("DD/MM/YYYY") : getField("slaReminderDate").value)
                                            : null}</Typography>
                                    </Stack>

                                    <Stack direction="row">
                                        <Typography className={classes.labelStyle}>Assigned to</Typography>
                                        <Typography >{slaReminderSentToName}</Typography>
                                    </Stack>
                                </Stack>
                            </Box>
                        )}
                    </Box>
                )}

                {isCurrentSla &&
                    automatedReminderItems.length > 0 &&
                    slaInfo.companyRelationshipManagerID && (
                        <Box className={classes.sectionCard}>
                            <Title backButtonEnabled={false} title="Automated Reminders" />

                            <Box className={classes.automatedReminderWrapper}>
                                {automatedReminderItems.map((item, index) => (
                                    <Box
                                        key={item.key}
                                        className={`${classes.automatedReminderRow} ${index !== automatedReminderItems.length - 1
                                            ? classes.automatedReminderRowBorder
                                            : ""
                                            }`}
                                    >
                                        <Box className={classes.automatedReminderIcon}>✓</Box>
                                        <Typography className={classes.automatedReminderText}>
                                            {item.text}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}

                {currentStatusConfig.docs !== "hidden" && (
                    <Box className={classes.sectionCard}>
                        <Box className={classes.tabsHeader}>
                            <Tabs value={sectionTab} onChange={handleSectionTabChange}>
                                <Tab label="Documents" sx={{ textTransform: "none" }} />
                                <Tab
                                    label="SLA review actions"
                                    sx={{ textTransform: "none" }}
                                />
                            </Tabs>
                        </Box>

                        {sectionTab === 0 && (
                            <DocumentsTab
                                currentStatus={currentStatus}
                                showSectionErrors={showSectionErrors}
                                onCurrentDocumentChange={setHasCurrentDocument}
                                onDocumentsChange={setSlaDocuments}
                                expiryDate={getField("slaExpiryDate").value}
                                onSyncExpiryDate={syncExpiryDateFromDocument}   // new prop
                                onRemoveDocument={() => {
                                    getField("slaStatusID").set("value", SLAStatus.CurrentSLAPendingDocumentUpload);
                                }}
                            />
                        )}

                        {sectionTab === 1 && (
                            <ReviewActionsTab
                                currentStatus={currentStatus}
                                companyOverview={companyOverview}
                            />
                        )}
                    </Box>
                )}

                <Box className={classes.sectionTitle}>
                    <Title backButtonEnabled={false} title="SLA Comments" />
                    <SLAComments isSlaRelated={true} priceReview={true} onSyncPriceReviewDate={syncPriceReviewDateFromDocument} />
                </Box>
            </Box>
        </>
    );
};

export default observer(SLAManagement);
